import React, { Dispatch, SetStateAction, useEffect, useMemo, useState } from "react";

import { MenuItem } from "@mui/material";
import classnames from "classnames";
import { useTranslation } from "react-i18next";
import { FiEyeOff, FiSearch } from "react-icons/fi";

import { useAppState } from "../../../../../context/StateContext";
import { useVideoSurveyInventory } from "../../../../../hooks/useVideoSurveyInventory";
import { Destination } from "../../../../../models/destination";
import { Location } from "../../../../../models/location";
import { getColorByDestination } from "../../../../../util/getColorByDestination";
import { getDestinations } from "../../../../../util/getDestinations";
import Select from "../../../../Form/Select";
import TabsListSection from "../../../../Generic/QuotedInventory/TabsListSection";
import UITabMaterial, { PanelProps } from "../../../../Generic/UITabMaterial";
import useLocalVideoToggle from "../../../hooks/useLocalVideoToggle/useLocalVideoToggle";
import useVideoContext from "../../../hooks/useVideoContext/useVideoContext";
import useSurveyStyles from "../../styles";
import useStyles, { sectionStyle } from "../styles";
import Details from "./components/General";
import { useTabComponentActions } from "./useActions";

interface Props {
  setIsCollapsed: Dispatch<SetStateAction<boolean>>;
  currentTab: number;
  setCurrentTab: (value: number) => void;
}

const TabComponent = ({ setIsCollapsed, setCurrentTab, currentTab }: Props) => {
  const { currentUser } = useAppState();
  const { t: translate } = useTranslation();
  const { selectedRoomName, job, destinations } = useVideoSurveyInventory();
  const { isSharingScreen } = useVideoContext();
  const { handleAddRoom, handleAddItemToRoom, handleAddMaterialToRoom } = useTabComponentActions();
  const [isVideoEnabled] = useLocalVideoToggle();

  const surveyClasses = useSurveyStyles();
  const classes = useStyles();
  const sectionClasses = sectionStyle(isVideoEnabled, isSharingScreen)();

  const [destinationsOptions, setDestinationsOptions] = useState<Destination[]>([]);
  const [destination, setDestination] = useState<Destination>();

  const locations = useMemo(
    () => [...((job?.destination?.locations as Location[]) ?? [])],
    [job?.destination]
  );

  const isResidence = (destination?: Destination) =>
    destination && destination.destinationResidenceId ? destination : undefined;
  const isNotResidence = (destination?: Destination) =>
    destination && !destination.destinationResidenceId ? destination : undefined;

  const renderDestinationSelect: React.ReactChild = (
    <Select
      className={classes.lighterBorder}
      style={{
        color: getColorByDestination(isNotResidence(destination), isResidence(destination)),
      }}
      data-cy="destinationSelect"
      name="destination"
      value={destination?.name}
      onChange={(event) => {
        const dest = destinationsOptions.find(
          (destination) => destination.name === event.target.value
        );

        if (dest) setDestination(dest);
      }}
      displayEmpty
      placeholder={translate("selectAnOption")}
    >
      {destinationsOptions?.map((destination) => (
        <MenuItem key={destination.name} value={destination.name}>
          {destination?.name}
        </MenuItem>
      ))}
    </Select>
  );

  const panels: PanelProps[] = [
    {
      menuItem: translate("details"),
      className: sectionClasses.tabClass,
      tabClassName: classes.tabHeaderClass,
      key: "detailsPanelTab",
      "data-cy": "detailsTab",
      render: () => <Details job={job} />,
    },
    {
      menuItem: translate("inventory"),
      className: sectionClasses.tabClass,
      tabClassName: classes.tabHeaderClass,
      key: "inventoryPanelTab",
      "data-cy": "inventoryTab",
      render: () =>
        job &&
        job.id && (
          <TabsListSection
            jobId={job?.id}
            rightIcon={<FiSearch size={16} color="#D0D5DD" />}
            destination={renderDestinationSelect}
            addRoom={(room) => handleAddRoom(room)}
            addItemIntoRoom={(item) => {
              handleAddItemToRoom(
                item,
                selectedRoomName || "",
                currentUser?.company,
                destination && !destination?.destinationResidenceId ? destination : undefined,
                destination && destination?.destinationResidenceId
                  ? locations?.find(
                      (location) => location.id === destination?.destinationResidenceId
                    )
                  : undefined
              );
            }}
            addMaterialIntoRoom={(service) =>
              handleAddMaterialToRoom(
                selectedRoomName || "",
                service,
                currentUser?.company,
                destination && !destination?.destinationResidenceId ? destination : undefined,
                destination && destination?.destinationResidenceId
                  ? locations?.find(
                      (location) => location.id === destination?.destinationResidenceId
                    )
                  : undefined
              )
            }
          />
        ),
    },
    // TODO Camila: wating for estimate screen to be ready
    // {
    //   menuItem: translate("estimate"),
    //   className: sectionClasses.tabClass,
    //   tabClassName: classes.tabHeaderClass,
    //   key: "estimatePanelTab",
    //   "data-cy": "estimateTab",
    //   render: () => <Estimate job={job} />,
    // },
    {
      key: "eyePanelTab",
      "data-cy": "collapseTabSection",
      icon: <FiEyeOff size={18} />,
      iconAction: () => setIsCollapsed(true),
      tabClassName: classes.iconTabClass,
    },
  ];

  useEffect(() => {
    if (destinations) {
      const destination = getDestinations(destinations, job?.destination?.locations);

      setDestinationsOptions(destination);
      setDestination(destination[0]);
    }
  }, [destinations]);

  return (
    <div className={classnames(surveyClasses.section, sectionClasses.mainSection)}>
      <UITabMaterial
        leftPanelClassName={sectionClasses.removePaddingTop}
        leftPanels={panels}
        onChangeLeftPanel={(e, tab) => setCurrentTab(tab)}
        selectedLeftPanel={currentTab}
      />
    </div>
  );
};

export default TabComponent;
